/** Client-only shell prefs (localStorage). No backend. */

const COLLAPSED_KEY = 'founder-os-shell-collapsed';
const MODE_KEY = 'founder-os-shell-mode';
const PINNED_KEY = 'founder-os-shell-pinned';
const FAV_KEY = 'founder-os-shell-favorites';
const RECENT_KEY = 'founder-os-shell-recent';
const SELLER_KEY = 'founder-os-shell-seller-label';
const MARKET_KEY = 'founder-os-shell-marketplace';
const CONN_STATUS_KEY = 'founder-os-shell-connection-status';
const SYNC_KEY = 'founder-os-shell-last-sync';

import type { ShellNavMode } from './navConfig';

function readJsonArray(key: string): string[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const v = JSON.parse(raw) as unknown;
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function writeJsonArray(key: string, paths: string[]) {
  try {
    localStorage.setItem(key, JSON.stringify(paths.slice(0, 20)));
  } catch {
    /* ignore */
  }
}

export function getCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function setCollapsed(v: boolean) {
  try {
    localStorage.setItem(COLLAPSED_KEY, v ? '1' : '0');
  } catch {
    /* ignore */
  }
}

export function getNavMode(): ShellNavMode {
  try {
    return localStorage.getItem(MODE_KEY) === 'engineer' ? 'engineer' : 'founder';
  } catch {
    return 'founder';
  }
}

export function setNavMode(mode: ShellNavMode) {
  try {
    localStorage.setItem(MODE_KEY, mode);
  } catch {
    /* ignore */
  }
}

export function getPinnedPaths(): string[] {
  return readJsonArray(PINNED_KEY);
}

export function togglePinned(path: string): string[] {
  const cur = getPinnedPaths();
  const next = cur.includes(path) ? cur.filter((p) => p !== path) : [path, ...cur].slice(0, 10);
  writeJsonArray(PINNED_KEY, next);
  return next;
}

export function getFavoritePaths(): string[] {
  return readJsonArray(FAV_KEY);
}

export function toggleFavorite(path: string): string[] {
  const cur = getFavoritePaths();
  const next = cur.includes(path) ? cur.filter((p) => p !== path) : [path, ...cur].slice(0, 10);
  writeJsonArray(FAV_KEY, next);
  return next;
}

export function getRecentPaths(): string[] {
  return readJsonArray(RECENT_KEY);
}

export function pushRecent(path: string): string[] {
  if (!path || path === '/login' || path.startsWith('/login')) return getRecentPaths();
  const next = [path, ...getRecentPaths().filter((p) => p !== path)].slice(0, 12);
  writeJsonArray(RECENT_KEY, next);
  return next;
}

export type ShellContextSnapshot = {
  workspaceId: string | null;
  tenantId: string | null;
  orgId: string | null;
  sellerLabel: string;
  marketplace: string;
  connectionStatus: 'unknown' | 'connected' | 'disconnected' | 'error';
  lastSync: string | null;
  environment: string;
};

export function readContextLabels(): Pick<
  ShellContextSnapshot,
  'sellerLabel' | 'marketplace' | 'connectionStatus' | 'lastSync'
> {
  try {
    const st = localStorage.getItem(CONN_STATUS_KEY);
    const connectionStatus =
      st === 'connected' || st === 'disconnected' || st === 'error' || st === 'unknown'
        ? st
        : 'unknown';
    return {
      sellerLabel: localStorage.getItem(SELLER_KEY) || '—',
      marketplace: localStorage.getItem(MARKET_KEY) || 'Amazon',
      connectionStatus,
      lastSync: localStorage.getItem(SYNC_KEY),
    };
  } catch {
    return { sellerLabel: '—', marketplace: 'Amazon', connectionStatus: 'unknown', lastSync: null };
  }
}

/** Shell switchers write labels only (no API). */
export function writeContextLabels(partial: {
  sellerLabel?: string;
  marketplace?: string;
  connectionStatus?: ShellContextSnapshot['connectionStatus'];
  lastSync?: string | null;
}) {
  try {
    if (partial.sellerLabel != null) localStorage.setItem(SELLER_KEY, partial.sellerLabel);
    if (partial.marketplace != null) localStorage.setItem(MARKET_KEY, partial.marketplace);
    if (partial.connectionStatus != null) localStorage.setItem(CONN_STATUS_KEY, partial.connectionStatus);
    if (partial.lastSync !== undefined) {
      if (partial.lastSync) localStorage.setItem(SYNC_KEY, partial.lastSync);
      else localStorage.removeItem(SYNC_KEY);
    }
  } catch {
    /* ignore */
  }
}
